import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Space, Spin, Typography } from 'antd';
import { CrownOutlined, GiftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import SignupLayout from './SignupLayout';
import ChoiceCard from './ChoiceCard';
import StepAccount from './StepAccount';
import StepParentRole from './StepParentRole';
import StepChildren from './StepChildren';
import StepSpouse from './StepSpouse';
import StepPayment from './StepPayment';
import type { PaymentMethod } from './StepPayment';
import type { Periodicite } from '../../../api/paiement/paiement';
import SignupDone from './SignupDone';
import { signupPalette } from './signupTheme';

import {
    useSignupApi,
    SIGNUP_STEPS,
    type ParentRole,
    type SignupConfig,
    type SignupPlan,
    type SignupResult,
} from '../../../api/auth/signup';
import { usePaiementApi, type CashplusInitiateResponse } from '../../../api/paiement/paiement';
import { useAuth } from '../../../context/AuthContext';
import { clearFunnelId, createSignupTracker, resolveFunnelId } from '../../../lib/signupFunnel';
import { effacerParrainage, lireParrainage, normaliserCodeApporteur } from '../../../lib/parrainage';

const { Text } = Typography;

/** Ordre des écrans premium — l'index dans ce tableau pilote la barre de progression. */
const PREMIUM_STEPS = ['account', 'parent_role', 'children', 'spouse', 'payment'] as const;
type PremiumStep = (typeof PREMIUM_STEPS)[number];

const DEFAULT_CONFIG: SignupConfig = {
    google_enabled: false,
    google_client_id: '',
    freemium_children_count: 2,
    max_premium_children: 10,
};

/** Lit l'email d'un ID token Google — affichage uniquement, la vérification est côté serveur. */
const emailFromGoogleToken = (idToken: string): string => {
    try {
        const payload = idToken.split('.')[1];
        if (!payload) return '';
        // base64url → base64, avec le padding qu'un JWT omet toujours.
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
        const bytes = Uint8Array.from(window.atob(padded), (ch) => ch.charCodeAt(0));
        const decoded = JSON.parse(new TextDecoder().decode(bytes));
        return typeof decoded?.email === 'string' ? decoded.email : '';
    } catch {
        return '';
    }
};

/** Renvoie les écoles vers le formulaire organisation, qui a ses propres champs. */
const OrganisationLink: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    const { t } = useTranslation();
    return (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
            <Button type="link" onClick={onClick} style={{ color: signupPalette.textSecondary, fontSize: 13 }}>
                {t('signup.organisationLink', 'Vous représentez une école ? Inscrivez votre établissement')}
            </Button>
        </div>
    );
};

const readErrorMessage = (err: any, fallback: string): string => {
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object') {
        if (typeof data.message === 'string' && data.message.trim()) return data.message;
        if (typeof data.error === 'string' && data.error.trim()) return data.error;
    }
    return err?.message || fallback;
};

/**
 * Tunnel d'inscription.
 *
 * Freemium : un seul écran (email ou Google) — deux comptes enfants sont créés
 * systématiquement. Premium : cinq écrans, une question par écran, paiement
 * compris. La création effective des comptes n'a lieu qu'au dernier écran :
 * un tunnel abandonné ne laisse donc aucun compte fantôme derrière lui.
 */
const SignupWizard: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const signupApi = useSignupApi();
    const paiementApi = usePaiementApi();
    const { refreshMe } = useAuth();

    // --- Plan & tunnel ---
    const planParam = (searchParams.get('plan') || '').toLowerCase();
    const [plan, setPlan] = useState<SignupPlan | null>(
        planParam === 'freemium' || planParam === 'premium' ? (planParam as SignupPlan) : null
    );
    const funnelId = useMemo(() => resolveFunnelId(searchParams.get('fid')), [searchParams]);
    const track = useMemo(() => createSignupTracker(plan ?? 'freemium', funnelId), [plan, funnelId]);

    // --- Configuration serveur (client Google, bornes) ---
    const [config, setConfig] = useState<SignupConfig | null>(null);

    // --- État du formulaire ---
    const [step, setStep] = useState<PremiumStep>('account');
    const [email, setEmail] = useState('');
    const [googleIdToken, setGoogleIdToken] = useState<string>('');
    const [googleEmail, setGoogleEmail] = useState<string>('');
    const [parentRole, setParentRole] = useState<ParentRole | undefined>();
    const [childrenCount, setChildrenCount] = useState(1);
    const [spouseEnabled, setSpouseEnabled] = useState<boolean | undefined>();
    const [spouseEmail, setSpouseEmail] = useState('');
    const [country, setCountry] = useState<string | undefined>();
    // Code animateur : pré-rempli par le dernier lien de parrainage cliqué
    // (moins de 30 jours), modifiable. La source dit s'il vient du lien.
    const parrainageInitial = useMemo(() => lireParrainage(), []);
    const [codeApporteur, setCodeApporteur] = useState(parrainageInitial?.code ?? '');
    // Aucun code d'etablissement dans le tunnel : une famille dont l'ecole est
    // partenaire a deja des comptes, crees a l'import, et s'inscrire lui en
    // creerait de nouveaux. Le tarif negocie s'applique depuis le menu
    // paiement, pour un parent deja connecte.

    // --- Résultat ---
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [result, setResult] = useState<SignupResult | null>(null);
    const [cashplus, setCashplus] = useState<CashplusInitiateResponse | null>(null);
    const [paymentError, setPaymentError] = useState<string | undefined>();
    const [currency, setCurrency] = useState<string>('');
    const [sessionReady, setSessionReady] = useState(false);

    const completedRef = useRef(false);

    useEffect(() => {
        let cancelled = false;
        signupApi
            .getConfig()
            .then((cfg) => {
                if (!cancelled) setConfig(cfg);
            })
            .catch(() => {
                // Sans configuration serveur on reste utilisable : email uniquement.
                if (!cancelled) setConfig(DEFAULT_CONFIG);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Trace l'entrée dans le tunnel, puis l'abandon éventuel.
    useEffect(() => {
        if (!plan) return;
        track(SIGNUP_STEPS.LANDING_CTA);
        const onLeave = () => {
            if (!completedRef.current) track(SIGNUP_STEPS.ABANDONED, { metadata: { last_step: stepRef.current } });
        };
        window.addEventListener('beforeunload', onLeave);
        return () => window.removeEventListener('beforeunload', onLeave);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plan]);

    // Conserve l'étape courante hors closure pour le traceur d'abandon.
    const stepRef = useRef<PremiumStep>('account');
    useEffect(() => {
        stepRef.current = step;
    }, [step]);

    const effectiveMethod = googleIdToken ? 'google' : 'email';
    const effectiveEmail = googleIdToken ? googleEmail : email.trim();

    const handleGoogleCredential = useCallback(
        (idToken: string) => {
            setGoogleIdToken(idToken);
            setGoogleEmail(emailFromGoogleToken(idToken));
            setError(undefined);
        },
        []
    );

    const clearGoogle = useCallback(() => {
        setGoogleIdToken('');
        setGoogleEmail('');
    }, []);

    /** Crée les comptes. Renvoie le résultat, ou null si l'appel a échoué. */
    const runSignup = useCallback(
        async (payloadCountry?: string): Promise<SignupResult | null> => {
            setError(undefined);
            setSubmitting(true);
            try {
                const created = await signupApi.signup({
                    plan: plan as SignupPlan,
                    method: effectiveMethod,
                    email: effectiveMethod === 'email' ? effectiveEmail : undefined,
                    google_id_token: effectiveMethod === 'google' ? googleIdToken : undefined,
                    parent_role: plan === 'premium' ? parentRole : undefined,
                    children_count: plan === 'premium' ? childrenCount : undefined,
                    spouse: plan === 'premium' && spouseEnabled && spouseEmail.trim() ? { email: spouseEmail.trim() } : null,
                    country: payloadCountry ?? country,
                    funnel_id: funnelId,
                    code_apporteur: codeApporteur.trim() || undefined,
                    code_apporteur_source:
                        parrainageInitial && normaliserCodeApporteur(codeApporteur) === parrainageInitial.code
                            ? 'lien'
                            : 'saisie',
                });

                completedRef.current = true;
                setResult(created);
                clearFunnelId();
                // Le code a servi : le lien de parrainage ne doit pas resservir
                // a une autre inscription depuis ce navigateur.
                effacerParrainage();

                // Avec Google, l'email est déjà vérifié : on ouvre la session tout
                // de suite pour que « Accéder à mon espace » fonctionne d'un clic.
                if (effectiveMethod === 'google') {
                    try {
                        await signupApi.loginWithGoogle(googleIdToken);
                        await refreshMe();
                        setSessionReady(true);
                    } catch {
                        setSessionReady(false);
                    }
                }
                return created;
            } catch (err: any) {
                setError(readErrorMessage(err, t('signup.errors.generic', 'La création du compte a échoué. Réessayez.')));
                // Email déjà pris : la seule correction possible est à l'étape 1.
                // On y renvoie l'utilisateur plutôt que de le laisser bloqué sur
                // l'écran de paiement avec un message qu'il ne peut pas résoudre.
                if (err?.response?.status === 409) {
                    setStep('account');
                }
                return null;
            } finally {
                setSubmitting(false);
            }
        },
        [
            signupApi,
            plan,
            effectiveMethod,
            effectiveEmail,
            googleIdToken,
            parentRole,
            childrenCount,
            spouseEnabled,
            spouseEmail,
            country,
            funnelId,
            codeApporteur,
            parrainageInitial,
            refreshMe,
            t,
        ]
    );

    /** Étape 5 : création des comptes puis génération du code CashPlus. */
    const handlePay = useCallback(
        async (params: {
            months: number;
            country: string;
            amountPerAccount: number;
            currency: string;
            method: PaymentMethod;
            /** Rythme de prélèvement — carte uniquement. */
            periodicite?: Periodicite;
        }) => {
            setCountry(params.country);
            setCurrency(params.currency);
            track(SIGNUP_STEPS.PAYMENT, { country: params.country, metadata: { months: params.months } });

            const created = await runSignup(params.country);
            if (!created) return;

            if (!created.checkout_token || !created.billable_user_ids?.length) {
                setPaymentError(
                    t(
                        'signup.errors.checkoutUnavailable',
                        'Le paiement n’a pas pu être lancé automatiquement. Connectez-vous pour le finaliser depuis la page Paiement.'
                    )
                );
                return;
            }

            const now = new Date();
            const end = new Date(now);
            end.setMonth(end.getMonth() + params.months);

            setSubmitting(true);
            try {
                // Carte bancaire : on quitte le tunnel pour la page hebergee.
                // Le retour se fait sur LEMONSQUEEZY_REDIRECT_URL et ce sont les
                // webhooks qui activent les comptes — deja crees a ce stade.
                if (params.method === 'lemonsqueezy') {
                    const { data } = await paiementApi.initiateLemonsqueezy(
                        {
                            billing: {
                                request_id: crypto.randomUUID(),
                                generated_at: now.toISOString(),
                                expiration_date: end.toISOString(),
                                details: created.billable_user_ids.map((id) => ({
                                    student_id: id,
                                    start_date: now.toISOString(),
                                    end_date: end.toISOString(),
                                })),
                            },
                            // Le code applique le tarif negocie de l'ecole ; il
                            // ne dispense pas de payer. Le montant reste
                            // calcule par le serveur.
                            periodicite: params.periodicite,
                        },
                        created.checkout_token
                    );
                    track(SIGNUP_STEPS.CHECKOUT, { country: params.country, metadata: { method: 'lemonsqueezy' } });
                    window.location.assign(data.checkout_url);
                    return;
                }

                const { data } = await paiementApi.initiateCashplus(
                    {
                        billing: {
                            generated_at: now.toISOString(),
                            expiration_date: end.toISOString(),
                            details: created.billable_user_ids.map((id) => ({
                                student_id: id,
                                start_date: now.toISOString(),
                                end_date: end.toISOString(),
                                amount: params.amountPerAccount,
                            })),
                        },
                    },
                    created.checkout_token
                );
                setCashplus(data);
                track(SIGNUP_STEPS.CHECKOUT, { country: params.country, metadata: { amount: data.total_amount } });
            } catch (err: any) {
                setPaymentError(
                    readErrorMessage(
                        err,
                        t(
                            'signup.errors.payment',
                            'Le code de paiement n’a pas pu être généré. Vos comptes sont créés : finalisez depuis la page Paiement.'
                        )
                    )
                );
            } finally {
                setSubmitting(false);
            }
        },
        [runSignup, paiementApi, track, t]
    );

    // ------------------------------------------------------------
    // Rendu
    // ------------------------------------------------------------

    // Choix de la formule quand l'URL n'en porte pas (accès direct à /register).
    if (!plan) {
        return (
            <SignupLayout
                title={t('signup.plan.title', 'Quelle formule souhaitez-vous ?')}
                subtitle={t('signup.plan.subtitle', 'Vous pourrez changer d’avis plus tard.')}
            >
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <ChoiceCard
                            icon={<GiftOutlined />}
                            label={t('signup.plan.freemium', 'Freemium')}
                            description={t('signup.plan.freemiumHint', 'Gratuit — 2 comptes enfants.')}
                            selected={false}
                            onSelect={() => setPlan('freemium')}
                        />
                        <ChoiceCard
                            icon={<CrownOutlined />}
                            label={t('signup.plan.premium', 'Premium')}
                            description={t('signup.plan.premiumHint', 'Nombre d’enfants au choix, compte conjoint inclus.')}
                            selected={false}
                            onSelect={() => setPlan('premium')}
                        />
                    </div>
                    <OrganisationLink onClick={() => navigate('/register-organisation')} />
                </Space>
            </SignupLayout>
        );
    }

    if (!config) {
        return (
            <SignupLayout title={t('signup.loading', 'Préparation de votre inscription…')}>
                <div style={{ textAlign: 'center', padding: 32 }}>
                    <Spin size="large" />
                </div>
            </SignupLayout>
        );
    }

    // Écran final commun aux deux formules.
    if (result) {
        return (
            <SignupLayout title={t('signup.done.layoutTitle', 'Bienvenue chez Tilmicode')}>
                <SignupDone
                    result={result}
                    cashplus={cashplus}
                    currency={currency}
                    sessionReady={sessionReady}
                    paymentError={paymentError}
                    onEnterApp={() => navigate('/')}
                    onGoToLogin={() => navigate('/login')}
                />
            </SignupLayout>
        );
    }

    const googleClientId = config.google_enabled ? config.google_client_id : undefined;

    // ---- Freemium : un seul écran ----
    if (plan === 'freemium') {
        return (
            <SignupLayout
                title={t('signup.freemium.title', 'Créez votre espace famille')}
                subtitle={t(
                    'signup.freemium.subtitle',
                    'Votre email et votre pays suffisent. Nous créons vos {{count}} comptes enfants automatiquement.',
                    { count: config.freemium_children_count }
                )}
            >
                <StepAccount
                    plan="freemium"
                    email={email}
                    onEmailChange={setEmail}
                    googleEmail={googleEmail || undefined}
                    googleClientId={googleClientId}
                    onGoogleCredential={handleGoogleCredential}
                    onClearGoogle={clearGoogle}
                    submitting={submitting}
                    freemiumChildrenCount={config.freemium_children_count}
                    codeApporteur={codeApporteur}
                    onCodeApporteurChange={setCodeApporteur}
                    error={error}
                    // Le freemium ne passe jamais par l'écran de paiement : c'est
                    // ici, et seulement ici, qu'on peut connaître son pays.
                    showCountry
                    country={country}
                    onCountryChange={setCountry}
                    onContinue={() => {
                        track(SIGNUP_STEPS.ACCOUNT, { method: effectiveMethod, email: effectiveEmail, country });
                        void runSignup();
                    }}
                />
                <OrganisationLink onClick={() => navigate('/register-organisation')} />
            </SignupLayout>
        );
    }

    // ---- Premium : cinq écrans ----
    const stepIndex = PREMIUM_STEPS.indexOf(step) + 1;
    const goTo = (next: PremiumStep) => {
        setError(undefined);
        setStep(next);
    };

    const premiumScreens: Record<PremiumStep, { title: string; subtitle?: string; label: string; body: React.ReactNode; back?: () => void }> = {
        account: {
            title: t('signup.premium.accountTitle', 'Commençons par votre adresse email'),
            subtitle: t('signup.premium.accountSubtitle', 'Aucun mot de passe à choisir maintenant.'),
            label: t('signup.steps.account', 'Votre identifiant'),
            body: (
                <StepAccount
                    plan="premium"
                    email={email}
                    onEmailChange={setEmail}
                    googleEmail={googleEmail || undefined}
                    googleClientId={googleClientId}
                    onGoogleCredential={handleGoogleCredential}
                    onClearGoogle={clearGoogle}
                    submitting={false}
                    freemiumChildrenCount={config.freemium_children_count}
                    codeApporteur={codeApporteur}
                    onCodeApporteurChange={setCodeApporteur}
                    error={error}
                    onContinue={() => {
                        track(SIGNUP_STEPS.ACCOUNT, { method: effectiveMethod, email: effectiveEmail });
                        goTo('parent_role');
                    }}
                />
            ),
        },
        parent_role: {
            title: t('signup.premium.roleTitle', 'Vous êtes le papa ou la maman ?'),
            label: t('signup.steps.parentRole', 'Votre rôle'),
            back: () => goTo('account'),
            body: (
                <StepParentRole
                    value={parentRole}
                    onChange={setParentRole}
                    onContinue={() => {
                        track(SIGNUP_STEPS.PARENT_ROLE, { method: effectiveMethod, metadata: { parent_role: parentRole } });
                        goTo('children');
                    }}
                />
            ),
        },
        children: {
            title: t('signup.premium.childrenTitle', 'Combien de comptes enfants créer ?'),
            label: t('signup.steps.children', 'Vos enfants'),
            back: () => goTo('parent_role'),
            body: (
                <StepChildren
                    value={childrenCount}
                    max={config.max_premium_children}
                    onChange={setChildrenCount}
                    onContinue={() => {
                        track(SIGNUP_STEPS.CHILDREN, { method: effectiveMethod, metadata: { children_count: childrenCount } });
                        goTo('spouse');
                    }}
                />
            ),
        },
        spouse: {
            title: t('signup.premium.spouseTitle', 'Créer aussi un compte pour votre conjoint ?'),
            label: t('signup.steps.spouse', 'Compte conjoint'),
            back: () => goTo('children'),
            body: (
                <StepSpouse
                    enabled={spouseEnabled}
                    email={spouseEmail}
                    parentEmail={effectiveEmail}
                    onToggle={setSpouseEnabled}
                    onEmailChange={setSpouseEmail}
                    onContinue={() => {
                        track(SIGNUP_STEPS.SPOUSE, { method: effectiveMethod, metadata: { spouse: spouseEnabled === true } });
                        goTo('payment');
                    }}
                />
            ),
        },
        payment: {
            title: t('signup.premium.paymentTitle', 'Dernière étape : le paiement'),
            subtitle: t('signup.premium.paymentSubtitle', 'Vos comptes seront créés à la validation.'),
            label: t('signup.steps.payment', 'Paiement'),
            back: () => goTo('spouse'),
            body: (
                <StepPayment
                    childrenCount={childrenCount}
                    country={country}
                    onCountryChange={setCountry}
                    submitting={submitting}
                    error={error}
                    onPay={handlePay}
                    onSkip={() => {
                        track(SIGNUP_STEPS.PAYMENT, { metadata: { skipped: true } });
                        void runSignup();
                    }}
                />
            ),
        },
    };

    const screen = premiumScreens[step];

    return (
        <SignupLayout
            title={screen.title}
            subtitle={screen.subtitle}
            step={stepIndex}
            totalSteps={PREMIUM_STEPS.length}
            stepLabel={screen.label}
            onBack={screen.back}
        >
            {screen.body}
            {step === 'account' && (
                <div style={{ marginTop: 16, textAlign: 'center' }}>
                    <Button type="link" onClick={() => setPlan('freemium')} style={{ color: signupPalette.textSecondary }}>
                        {t('signup.premium.switchToFreemium', 'Essayer d’abord la formule gratuite')}
                    </Button>
                    <Text style={{ display: 'block', fontSize: 12, color: signupPalette.textMuted }}>
                        {t('signup.premium.switchHint', '2 comptes enfants, sans paiement.')}
                    </Text>
                    <OrganisationLink onClick={() => navigate('/register-organisation')} />
                </div>
            )}
        </SignupLayout>
    );
};

export default SignupWizard;
